import { useEffect, useMemo, useState } from 'react';
import {
  getPricingChangeHistoryGrouped,
  type GroupedHistoryEntry,
  type GroupedHistoryStatus
} from '@/services/loanPricingService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('PricingChangeHistoryPanel', 'pricing-v2');

interface PricingChangeHistoryPanelProps {
  encompassLoanId: string;
  refreshKey?: number;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) {
    return '—';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function avatarBgFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return `oklch(0.58 0.10 ${hue})`;
}

function relativeAgo(iso: string | undefined): string {
  if (!iso) {
    return '';
  }
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) {
    return 'just now';
  }
  if (diffMin < 60) {
    return `${diffMin} min ago`;
  }
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) {
    return `${diffHrs}h ago`;
  }
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

function formatDate(iso: string | undefined): string {
  if (!iso) {
    return '—';
  }
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

function formatTime(iso: string | undefined): string {
  if (!iso) {
    return '';
  }
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}

// Status badge copy for grouped + legacy history. Legacy pseudo-groups get
// their own small map off auditStatus because `status === 'legacy'` is a catch-all.
function groupBadge(status: GroupedHistoryStatus, auditStatus?: string): { label: string; tone: 'green' | 'blue' | 'amber' | 'gray' | 'red' } {
  if (status === 'legacy') {
    switch (auditStatus) {
      case 'synced': return { label: 'Synced', tone: 'green' };
      case 'failed': return { label: 'Failed', tone: 'red' };
      case 'cancelled': return { label: 'Cancelled', tone: 'gray' };
      case 'superseded': return { label: 'Superseded', tone: 'gray' };
      case 'observed': return { label: 'Observed', tone: 'gray' };
      default: return { label: auditStatus ?? 'Legacy', tone: 'gray' };
    }
  }
  switch (status) {
    case 'validated':
      return { label: 'Saved', tone: 'green' };
    case 'pending':
    case 'pushing':
      return { label: 'Saving', tone: 'blue' };
    case 'waiting_unlock': return { label: 'Waiting unlock', tone: 'amber' };
    case 'waiting_retry': return { label: 'Retrying', tone: 'amber' };
    case 'cancelled': return { label: 'Cancelled', tone: 'gray' };
    case 'failed': return { label: 'Failed', tone: 'red' };
    default: return { label: status, tone: 'gray' };
  }
}

function badgeClassName(tone: 'green' | 'blue' | 'amber' | 'gray' | 'red'): string {
  // Matches the ephemeral Tailwind classes used by PendingChangesCard's status
  // badge so the panel and the save bar read consistently.
  switch (tone) {
    case 'green': return 'hist-badge hist-badge-green';
    case 'blue': return 'hist-badge hist-badge-blue';
    case 'amber': return 'hist-badge hist-badge-amber';
    case 'gray': return 'hist-badge hist-badge-gray';
    case 'red': return 'hist-badge hist-badge-red';
  }
}

/**
 * Collapsed-by-default grouped pricing history panel (2B.12).
 * Renders one row per pricing_write_group (Phase 2) plus one row per legacy
 * audit (pre-Phase-2 single-field writes) from the unified service shape.
 */
export function PricingChangeHistoryPanel({ encompassLoanId, refreshKey }: PricingChangeHistoryPanelProps) {
  const [entries, setEntries] = useState<GroupedHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPricingChangeHistoryGrouped(encompassLoanId)
      .then((rows) => {
        if (!cancelled) {
          setEntries(rows);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.error('Failed to load pricing history', { encompassLoanId }, err as Error);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [encompassLoanId, refreshKey]);

  const latest = entries[0];
  const count = entries.length;

  const latestBlock = useMemo(() => {
    if (!latest) {
      return null;
    }
    const name = latest.actorName || latest.actorEmail || 'Unknown';
    const fieldCount = latest.changes.length;
    const summary = fieldCount === 1
      ? latest.changes[0].fieldLabel
      : `${fieldCount} fields`;
    return (
      <span className="hist-latest">
        <span className="hist-latest-label">Latest:</span>
        <span className="avatar xs" style={{ background: avatarBgFor(name) }}>
          {initials(name)}
        </span>
        <span>
          {name} changed <b>{summary}</b>
        </span>
        <span className="hist-latest-time">· {relativeAgo(latest.createdAt)}</span>
      </span>
    );
  }, [latest]);

  if (!loading && count === 0) {
    return null;
  }

  return (
    <section id="pv2-history" className="history-section" aria-label="Pricing change history">
      <button
        type="button"
        className="history-toggle"
        aria-expanded={open}
        aria-controls="pv2-history-body"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="hist-toggle-left">
          <svg className="hist-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M9 6l6 6-6 6" />
          </svg>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M12 8v4l3 2" />
            <circle cx="12" cy="12" r="10" />
          </svg>
          <span className="hist-toggle-label">Pricing Change History</span>
          <span className="hist-count">{count}</span>
        </span>
        <span className="hist-toggle-right">{latestBlock}</span>
      </button>
      <div className="history-body" id="pv2-history-body" hidden={!open}>
        <div className="history-table history-table-grouped">
          <div className="hist-head hist-head-grouped">
            <div>Date</div>
            <div>Changed By</div>
            <div>Status</div>
            <div>Changes</div>
            <div>Reason</div>
          </div>
          {entries.map((entry, entryIdx) => {
            const name = entry.actorName || entry.actorEmail || 'Unknown';
            const badge = groupBadge(entry.status, entry.auditStatus);
            const rowKey = entry.groupId ?? `legacy-${entry.createdAt}-${entryIdx}`;
            return (
              <div className="hist-row hist-row-grouped" key={rowKey}>
                <div className="hist-date">
                  <div className="mono">{formatDate(entry.createdAt)}</div>
                  <div className="hist-time">{formatTime(entry.createdAt)}</div>
                </div>
                <div className="hist-user">
                  <span className="avatar" style={{ background: avatarBgFor(name) }}>
                    {initials(name)}
                  </span>
                  <div>
                    <div>{name}</div>
                    {entry.actorEmail && <div className="hist-meta mono">{entry.actorEmail}</div>}
                  </div>
                </div>
                <div>
                  <span className={badgeClassName(badge.tone)}>{badge.label}</span>
                </div>
                <div className="hist-changes-list">
                  {entry.changes.map((change, changeIdx) => (
                    <div className="hist-change-row" key={`${rowKey}-${change.fieldId}-${changeIdx}`}>
                      <span className="field-pill">{change.fieldLabel}</span>
                      <span className="mono tab">{change.previousValue ?? 'Blank'}</span>
                      <svg className="arrow" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                      <span className="mono tab emp">{change.newValue ?? 'Blank'}</span>
                    </div>
                  ))}
                </div>
                <div className={`hist-reason${entry.changeReason ? '' : ' hist-empty'}`}>
                  {entry.changeReason || '—'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
